import { jsPDF } from 'jspdf';
import {
  DesignSpec,
  ArchitectureOutput,
  TechnicalOutput,
} from '../services/workflow';
import type { AgentPrompt } from '../services/admin/AdminService';

export interface ProblemStatementData {
  title: string;
  problem: string;
  target_users: string;
  success_criteria: string;
  current_state: string;
  integrations: string[];
  constraints: string[];
}

/**
 * Converts a title to a safe filename (kebab-case, no special characters).
 */
const toSafeFilename = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 50); // Limit length
};

/**
 * Exports a Problem Statement draft to PDF format.
 * Uses jsPDF to generate a clean, formatted PDF document.
 * The filename is derived from the title field.
 */
export const exportProblemStatementToPdf = (
  data: ProblemStatementData
): void => {
  const title = data.title || 'Problem Statement';
  const filename = `${toSafeFilename(title) || 'problem-statement'}.pdf`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper to add text with word wrap and page breaks
  const addWrappedText = (
    text: string,
    y: number,
    fontSize: number = 11,
    isBold: boolean = false
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');

    const lines = doc.splitTextToSize(text || '[To be refined]', contentWidth);
    const lineHeight = fontSize * 0.5;

    for (const line of lines) {
      // Check if we need a new page
      if (y + lineHeight > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }

    return y;
  };

  // Helper to add a section
  const addSection = (heading: string, content: string, y: number): number => {
    // Add heading
    y = addWrappedText(heading, y, 13, true);
    y += 2;

    // Add content
    y = addWrappedText(content, y, 11, false);
    y += 8;

    return y;
  };

  // Helper to add a list section
  const addListSection = (
    heading: string,
    items: string[],
    y: number
  ): number => {
    // Add heading
    y = addWrappedText(heading, y, 13, true);
    y += 2;

    if (!items || items.length === 0) {
      y = addWrappedText('- [To be refined]', y, 11, false);
    } else {
      for (const item of items) {
        y = addWrappedText(`• ${item}`, y, 11, false);
      }
    }

    y += 8;
    return y;
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, yPos);
  yPos += 12;

  // Add a subtle line under the title
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Sections
  yPos = addSection('Core Problem', data.problem, yPos);
  yPos = addSection('Target Users', data.target_users, yPos);
  yPos = addSection('Success Criteria', data.success_criteria, yPos);
  yPos = addSection('Current State', data.current_state, yPos);
  yPos = addListSection('Required Integrations', data.integrations, yPos);
  yPos = addListSection('Constraints', data.constraints, yPos);

  // Footer with generation date
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Generated on ${dateStr} by AI Agent Design Tool`,
    margin,
    doc.internal.pageSize.getHeight() - 10
  );

  // Save the PDF
  doc.save(filename);
};

/**
 * Exports a Design Spec to PDF format.
 */
export const exportDesignSpecToPdf = (data: DesignSpec): void => {
  const title = data.agent_name || 'Agent Design Specification';
  const filename = `${toSafeFilename(title) || 'design-spec'}.pdf`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper to add text with word wrap and page breaks
  const addWrappedText = (
    text: string,
    y: number,
    fontSize: number = 11,
    isBold: boolean = false
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');

    const lines = doc.splitTextToSize(text || '[To be defined]', contentWidth);
    const lineHeight = fontSize * 0.5;

    for (const line of lines) {
      if (y + lineHeight > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }

    return y;
  };

  // Helper to add a section
  const addSection = (heading: string, content: string, y: number): number => {
    y = addWrappedText(heading, y, 13, true);
    y += 2;
    y = addWrappedText(content, y, 11, false);
    y += 8;
    return y;
  };

  // Helper to add a list section
  const addListSection = (
    heading: string,
    items: string[],
    y: number
  ): number => {
    y = addWrappedText(heading, y, 13, true);
    y += 2;

    if (!items || items.length === 0) {
      y = addWrappedText('- [To be defined]', y, 11, false);
    } else {
      for (const item of items) {
        y = addWrappedText(`• ${item}`, y, 11, false);
      }
    }

    y += 8;
    return y;
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, yPos);
  yPos += 12;

  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Agent Design Specification', margin, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 8;

  // Line under title
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Agent Identity Section
  yPos = addWrappedText('AGENT IDENTITY', yPos, 14, true);
  yPos += 4;
  yPos = addSection('Persona', data.persona, yPos);
  yPos = addSection('Communication Tone', data.tone, yPos);

  // Goals Section
  yPos = addWrappedText('GOALS', yPos, 14, true);
  yPos += 4;
  yPos = addSection('Primary Goal', data.primary_goal, yPos);
  yPos = addListSection('Secondary Goals', data.secondary_goals, yPos);

  // Capabilities Section
  yPos = addWrappedText('CAPABILITIES & BOUNDARIES', yPos, 14, true);
  yPos += 4;
  yPos = addListSection('Capabilities', data.capabilities, yPos);
  yPos = addListSection('Boundaries', data.boundaries, yPos);

  // Behavioral Guidelines Section
  yPos = addWrappedText('BEHAVIORAL GUIDELINES', yPos, 14, true);
  yPos += 4;
  yPos = addSection('Autonomy Level', data.autonomy_level, yPos);
  yPos = addSection('Error Handling', data.error_handling, yPos);

  // Footer
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Generated on ${dateStr} by AI Agent Design Tool`,
    margin,
    doc.internal.pageSize.getHeight() - 10
  );

  doc.save(filename);
};

/**
 * Exports an Architecture Spec to PDF format.
 */
export const exportArchitectureToPdf = (data: ArchitectureOutput): void => {
  const title = data.root_agent
    ? `${data.root_agent} Architecture`
    : 'Multi-Agent Architecture';
  const filename = `${toSafeFilename(title) || 'architecture-spec'}.pdf`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper to add text with word wrap and page breaks
  const addWrappedText = (
    text: string,
    y: number,
    fontSize: number = 11,
    isBold: boolean = false
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');

    const lines = doc.splitTextToSize(text || '[Not specified]', contentWidth);
    const lineHeight = fontSize * 0.5;

    for (const line of lines) {
      if (y + lineHeight > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }

    return y;
  };

  // Helper to add a section
  const addSection = (heading: string, content: string, y: number): number => {
    y = addWrappedText(heading, y, 13, true);
    y += 2;
    y = addWrappedText(content, y, 11, false);
    y += 8;
    return y;
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Multi-Agent Architecture', margin, yPos);
  yPos += 12;

  // Line under title
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Architecture Overview
  yPos = addWrappedText('ARCHITECTURE OVERVIEW', yPos, 14, true);
  yPos += 4;
  yPos = addSection(
    'Type',
    data.is_multi_agent ? 'Multi-Agent System' : 'Single Agent',
    yPos
  );
  yPos = addSection(
    'Orchestration Pattern',
    data.orchestration_pattern.charAt(0).toUpperCase() +
      data.orchestration_pattern.slice(1),
    yPos
  );
  yPos = addSection('Root Agent', data.root_agent || 'Not specified', yPos);

  // Agent Roster
  yPos = addWrappedText('AGENT ROSTER', yPos, 14, true);
  yPos += 4;

  const agents = data.agents || [];
  if (agents.length === 0) {
    yPos = addWrappedText('No agents defined yet.', yPos, 11, false);
    yPos += 8;
  } else {
    for (const agent of agents) {
      // Check page break
      if (yPos > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage();
        yPos = margin;
      }

      yPos = addWrappedText(agent.name, yPos, 12, true);
      yPos += 2;
      yPos = addWrappedText(`Role: ${agent.role}`, yPos, 11, false);
      yPos += 2;

      // Responsibilities
      const responsibilities = agent.responsibilities || [];
      if (responsibilities.length > 0) {
        yPos = addWrappedText('Responsibilities:', yPos, 11, true);
        for (const resp of responsibilities) {
          yPos = addWrappedText(`  • ${resp}`, yPos, 10, false);
        }
      }

      // Tools
      const tools = agent.tools || [];
      if (tools.length > 0) {
        yPos = addWrappedText(`Tools: ${tools.join(', ')}`, yPos, 10, false);
      }

      yPos += 6;
    }
  }

  // Communication Flows
  yPos = addWrappedText('COMMUNICATION FLOWS', yPos, 14, true);
  yPos += 4;

  const flows = data.communication_flows || [];
  if (flows.length === 0) {
    yPos = addWrappedText('No communication flows defined.', yPos, 11, false);
    yPos += 8;
  } else {
    for (const flow of flows) {
      yPos = addWrappedText(
        `• ${flow.from} → ${flow.to}: ${flow.trigger}`,
        yPos,
        11,
        false
      );
    }
    yPos += 8;
  }

  // Human Checkpoints
  yPos = addWrappedText('HUMAN CHECKPOINTS', yPos, 14, true);
  yPos += 4;

  const checkpoints = data.human_checkpoints || [];
  if (checkpoints.length === 0) {
    yPos = addWrappedText(
      'No human checkpoints required (fully autonomous).',
      yPos,
      11,
      false
    );
  } else {
    for (const checkpoint of checkpoints) {
      yPos = addWrappedText(`• ${checkpoint}`, yPos, 11, false);
    }
  }

  // Footer
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Generated on ${dateStr} by AI Agent Design Tool`,
    margin,
    doc.internal.pageSize.getHeight() - 10
  );

  doc.save(filename);
};

/**
 * Exports a Technical Spec to PDF format.
 */
export const exportTechnicalSpecToPdf = (data: TechnicalOutput): void => {
  const title = data.model
    ? `Technical Spec - ${data.model}`
    : 'Technical Architecture';
  const filename = `${toSafeFilename(title) || 'technical-spec'}.pdf`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper to add text with word wrap and page breaks
  const addWrappedText = (
    text: string,
    y: number,
    fontSize: number = 11,
    isBold: boolean = false
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');

    const lines = doc.splitTextToSize(text || '[Not specified]', contentWidth);
    const lineHeight = fontSize * 0.5;

    for (const line of lines) {
      if (y + lineHeight > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }

    return y;
  };

  // Helper to add a section
  const addSection = (heading: string, content: string, y: number): number => {
    y = addWrappedText(heading, y, 13, true);
    y += 2;
    y = addWrappedText(content, y, 11, false);
    y += 8;
    return y;
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Technical Architecture Specification', margin, yPos);
  yPos += 12;

  // Line under title
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Model Selection
  yPos = addWrappedText('MODEL SELECTION', yPos, 14, true);
  yPos += 4;
  yPos = addSection('Recommended Model', data.model || 'Not selected', yPos);
  yPos = addSection(
    'Rationale',
    data.model_rationale || 'No rationale provided.',
    yPos
  );

  // Tools & Functions
  yPos = addWrappedText('TOOLS & FUNCTIONS', yPos, 14, true);
  yPos += 4;

  const tools = data.tools || [];
  if (tools.length === 0) {
    yPos = addWrappedText('No tools defined yet.', yPos, 11, false);
    yPos += 8;
  } else {
    for (const tool of tools) {
      // Check page break
      if (yPos > doc.internal.pageSize.getHeight() - 50) {
        doc.addPage();
        yPos = margin;
      }

      yPos = addWrappedText(tool.name, yPos, 12, true);
      yPos += 2;
      yPos = addWrappedText(
        `Type: ${tool.type || 'function'} | Async: ${tool.async ? 'Yes' : 'No'}`,
        yPos,
        10,
        false
      );
      yPos = addWrappedText(
        tool.description || 'No description',
        yPos,
        10,
        false
      );

      const params = tool.suggested_params || [];
      if (params.length > 0) {
        yPos = addWrappedText(
          `Parameters: ${params.join(', ')}`,
          yPos,
          10,
          false
        );
      }

      yPos += 6;
    }
  }

  // Memory & Session Strategy
  yPos = addWrappedText('MEMORY & SESSION STRATEGY', yPos, 14, true);
  yPos += 4;
  yPos = addSection('Strategy', data.memory_strategy || 'Not specified', yPos);

  const sessionConfig = data.session_config || {
    service: 'InMemorySessionService',
    ttl_hours: 24,
    persistence_enabled: false,
  };
  yPos = addWrappedText('Session Configuration:', yPos, 12, true);
  yPos += 2;
  yPos = addWrappedText(
    `• Service: ${sessionConfig.service || 'InMemorySessionService'}`,
    yPos,
    11,
    false
  );
  yPos = addWrappedText(
    `• TTL: ${sessionConfig.ttl_hours || 24} hours`,
    yPos,
    11,
    false
  );
  yPos = addWrappedText(
    `• Persistence: ${sessionConfig.persistence_enabled ? 'Enabled' : 'Disabled'}`,
    yPos,
    11,
    false
  );
  yPos += 8;

  // Token Estimates
  const tokenEstimates = data.token_estimates || {};
  const monthlyEstimates = tokenEstimates.monthly_estimates;

  yPos = addWrappedText('TOKEN USAGE ESTIMATES', yPos, 14, true);
  yPos += 4;

  if (monthlyEstimates) {
    const formatNumber = (n: number | undefined) =>
      n !== undefined ? n.toLocaleString() : 'TBD';
    yPos = addWrappedText('Monthly Projections:', yPos, 12, true);
    yPos += 2;
    yPos = addWrappedText(
      `• Input Tokens: ${formatNumber(monthlyEstimates.input_tokens)}`,
      yPos,
      11,
      false
    );
    yPos = addWrappedText(
      `• Output Tokens: ${formatNumber(monthlyEstimates.output_tokens)}`,
      yPos,
      11,
      false
    );
    yPos = addWrappedText(
      `• Total Tokens: ${formatNumber(monthlyEstimates.total_tokens)}`,
      yPos,
      11,
      false
    );
    yPos += 4;
  }

  const optimizationTips = tokenEstimates.optimization_tips || [];
  if (optimizationTips.length > 0) {
    yPos = addWrappedText('Optimization Tips:', yPos, 12, true);
    yPos += 2;
    for (const tip of optimizationTips) {
      yPos = addWrappedText(`• ${tip}`, yPos, 11, false);
    }
  }
  yPos += 8;

  // Integration Patterns
  yPos = addWrappedText('INTEGRATION PATTERNS', yPos, 14, true);
  yPos += 4;

  const integrations = data.integration_patterns || [];
  if (integrations.length === 0) {
    yPos = addWrappedText(
      'Direct API calls with error handling.',
      yPos,
      11,
      false
    );
  } else {
    for (const pattern of integrations) {
      yPos = addWrappedText(`• ${pattern}`, yPos, 11, false);
    }
  }

  // Footer
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Generated on ${dateStr} by AI Agent Design Tool`,
    margin,
    doc.internal.pageSize.getHeight() - 10
  );

  doc.save(filename);
};

/**
 * Exports an Agent Instruction prompt to PDF format.
 */
export const exportAgentInstructionsToPdf = (data: AgentPrompt): void => {
  const title = data.title || data.agent_name || 'Agent Instructions';
  const filename = `${toSafeFilename(title) || 'agent-instructions'}.pdf`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Helper to add text with word wrap and page breaks
  const addWrappedText = (
    text: string,
    y: number,
    fontSize: number = 11,
    isBold: boolean = false
  ): number => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');

    const lines = doc.splitTextToSize(text || '[Not specified]', contentWidth);
    const lineHeight = fontSize * 0.5;

    for (const line of lines) {
      if (y + lineHeight > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }

    return y;
  };

  // Helper to add a section
  const addSection = (heading: string, content: string, y: number): number => {
    y = addWrappedText(heading, y, 13, true);
    y += 2;
    y = addWrappedText(content, y, 11, false);
    y += 8;
    return y;
  };

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, yPos);
  yPos += 12;

  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Agent Instructions', margin, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 8;

  // Line under title
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Version Information
  yPos = addWrappedText('VERSION INFORMATION', yPos, 14, true);
  yPos += 4;
  yPos = addSection('Current Version', data.current_version, yPos);
  yPos = addSection('Status', data.is_enabled ? 'Enabled' : 'Disabled', yPos);

  // Get current active version details
  const currentVersion = data.versions.find((v) => v.is_active);
  if (currentVersion) {
    yPos = addSection(
      'Version Description',
      currentVersion.description || 'No description',
      yPos
    );
    if (currentVersion.performance_notes) {
      yPos = addSection(
        'Performance Notes',
        currentVersion.performance_notes,
        yPos
      );
    }
    yPos = addSection(
      'Created',
      `${new Date(currentVersion.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })} by ${currentVersion.created_by || 'Unknown'}`,
      yPos
    );
  }

  // Model Configuration
  yPos = addWrappedText('MODEL CONFIGURATION', yPos, 14, true);
  yPos += 4;

  const modelConfig = data.ai_model_config || {};
  yPos = addSection('Model', modelConfig.model || 'gemini-2.0-flash', yPos);
  yPos = addSection(
    'Temperature',
    `${modelConfig.temperature ?? 0.7} (0 = Deterministic, 2 = Creative)`,
    yPos
  );
  yPos = addSection(
    'Max Output Tokens',
    `${modelConfig.max_output_tokens ?? 4096}`,
    yPos
  );

  // Placeholders
  if (data.placeholders && data.placeholders.length > 0) {
    yPos = addWrappedText('PLACEHOLDERS', yPos, 14, true);
    yPos += 4;
    yPos = addWrappedText(
      data.placeholders.map((p) => `{${p}}`).join(', '),
      yPos,
      11,
      false
    );
    yPos += 8;
  }

  // Prompt Template
  yPos = addWrappedText('PROMPT TEMPLATE', yPos, 14, true);
  yPos += 4;

  const promptTemplate =
    currentVersion?.prompt_template || 'No prompt template configured';

  // Use monospace font for the template
  doc.setFont('courier', 'normal');
  doc.setFontSize(9);

  const templateLines = doc.splitTextToSize(promptTemplate, contentWidth);
  const lineHeight = 4.5;

  for (const line of templateLines) {
    if (yPos + lineHeight > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      yPos = margin;
    }
    doc.text(line, margin, yPos);
    yPos += lineHeight;
  }

  // Footer
  const footerDateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(128, 128, 128);
  doc.text(
    `Generated on ${footerDateStr} by AI Agent Design Tool`,
    margin,
    doc.internal.pageSize.getHeight() - 10
  );

  doc.save(filename);
};
